import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DataProvider } from './contexts/DataContext';
import { QuizProvider } from './contexts/QuizContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import PrivateRoute from './components/auth/PrivateRoute';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ErrorBoundary from './components/common/ErrorBoundary';
import FloatingAudioPlayer from './components/FloatingAudioPlayer';
import PageLoader from './components/common/PageLoader';
import './styles/globals.css';

// Lazy load pages for faster initial load and navigation
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Contact = lazy(() => import('./pages/Contact'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SubjectPage = lazy(() => import('./pages/SubjectPage'));
const SubjectLessonsPage = lazy(() => import('./pages/SubjectLessonsPage'));
const LessonPage = lazy(() => import('./pages/LessonPage'));
const ReferencesPage = lazy(() => import('./pages/ReferencesPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const DebugDataPage = lazy(() => import('./pages/DebugDataPage'));
const CaseScenariosPage = lazy(() => import('./pages/CaseScenariosPage'));
const CaseScenarioDetailPage = lazy(() => import('./pages/CaseScenarioDetailPage'));
const QuizDashboard = lazy(() => import('./pages/quiz/QuizDashboard'));
const QuizPage = lazy(() => import('./pages/quiz/QuizPage'));
const QuizReview = lazy(() => import('./pages/quiz/QuizReview'));
const ExamsPage = lazy(() => import('./pages/ExamsPage'));
const ExamViewer = lazy(() => import('./pages/ExamViewer'));
const TranscriptPage = lazy(() => import('./pages/TranscriptPage'));

// Lazy load components
const LabsPage = lazy(() => import('./components/labs').then(m => ({ default: m.LabsPage })));
const LabViewer = lazy(() => import('./components/labs').then(m => ({ default: m.LabViewer })));
const EndocrineQuiz = lazy(() => import('./components/EndocrineQuiz'));
const EndocrineCases = lazy(() => import('./components/EndocrineCases'));
const EndocrineEssay = lazy(() => import('./components/EndocrineEssay'));
const EndocrineExamHub = lazy(() => import('./components/EndocrineExamHub'));
const ETMShorts = lazy(() => import('./components/ETMShorts'));

function App() {
  // Simple mobile viewport handling - NO orientation blocking
  useEffect(() => {
    // Prevent zoom on double tap (iOS Safari)
    let lastTouchEnd = 0;
    const preventZoom = (e) => {
      const now = (new Date()).getTime();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    // Handle viewport changes (keyboard open/close on mobile)
    const handleViewportChange = () => {
      // Set CSS custom property for real viewport height
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Apply minimal fixes - NO orientation blocking for now
    document.addEventListener('touchend', preventZoom, false);
    window.addEventListener('resize', handleViewportChange);

    // Set initial viewport height
    handleViewportChange();

    // Cleanup
    return () => {
      document.removeEventListener('touchend', preventZoom, false);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
          <ThemeProvider>
            <DataProvider>
              <QuizProvider>
                <AudioPlayerProvider>
                  <Router>
                    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors overflow-x-hidden relative">
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Public Routes */}
                          <Route path="/landing" element={<LandingPage />} />
                          <Route path="/terms" element={<><Header /><main className="min-h-screen pt-16"><Terms /></main><Footer /></>} />
                          <Route path="/privacy" element={<><Header /><main className="min-h-screen pt-16"><Privacy /></main><Footer /></>} />
                          <Route path="/contact" element={<><Header /><main className="min-h-screen pt-16"><Contact /></main><Footer /></>} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register" element={<SignupPage />} />

                          {/* Protected Routes */}
                          <Route
                            path="/dashboard"
                            element={
                              <PrivateRoute>
                                <Header />
                                <main className="min-h-screen">
                                  <Dashboard />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/subject/:subjectId"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <SubjectLessonsPage />
                                </main>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/lesson/:subjectId/:lessonId"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <LessonPage />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          {/* Case Scenarios Routes */}
                          <Route
                            path="/cases/:subjectId"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <CaseScenariosPage />
                                </main>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/case/:subjectId/:caseId"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <CaseScenarioDetailPage />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/references"
                            element={
                              <PrivateRoute>
                                <Header />
                                <main className="min-h-screen pt-16">
                                  <ReferencesPage />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/admin"
                            element={
                              <PrivateRoute requireAdmin>
                                <Header />
                                <main className="min-h-screen pt-16">
                                  <AdminPage />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/debug"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <DebugDataPage />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          {/* Quiz Routes */}
                          <Route
                            path="/quiz"
                            element={
                              <PrivateRoute>
                                <Header />
                                <main className="min-h-screen pt-16">
                                  <QuizDashboard />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/quiz/:subject"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <QuizPage />
                                </main>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/quiz/:subject/review"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <QuizReview />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          {/* Lab Routes */}
                          <Route
                            path="/labs"
                            element={
                              <PrivateRoute>
                                <Header />
                                <main className="min-h-screen pt-16">
                                  <LabsPage />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/labs/:subjectId"
                            element={
                              <PrivateRoute>
                                <Header />
                                <main className="min-h-screen pt-16">
                                  <LabsPage />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/lab/:subjectId/:labId"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <LabViewer />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          {/* Exam Routes */}
                          <Route
                            path="/exams"
                            element={
                              <PrivateRoute>
                                <Header />
                                <main className="min-h-screen pt-16">
                                  <ExamsPage />
                                </main>
                                <Footer />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/exams/:subject/:examId"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <ExamViewer />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          {/* Endocrine Exam Routes */}
                          <Route
                            path="/endocrine"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <EndocrineExamHub />
                                </main>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/endocrine/quiz"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <EndocrineQuiz />
                                </main>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/endocrine/cases"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <EndocrineCases />
                                </main>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/endocrine/essay"
                            element={
                              <PrivateRoute>
                                <main className="min-h-screen">
                                  <EndocrineEssay />
                                </main>
                              </PrivateRoute>
                            }
                          />

                          {/* ETM Shorts - TikTok-style Video Feed */}
                          <Route
                            path="/shorts"
                            element={
                              <PrivateRoute>
                                <ETMShorts />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/transcript/:subjectId"
                            element={
                              <PrivateRoute>
                                <TranscriptPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Default Route */}
                          <Route path="/" element={<Navigate to="/landing" replace />} />
                        </Routes>
                      </Suspense>
                      <FloatingAudioPlayer />
                    </div>
                  </Router>
                </AudioPlayerProvider>
              </QuizProvider>
            </DataProvider>
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;